import { InputNew } from '@lune-fe/lune-ui-lib'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'

import FilterItem from 'components/FilterItem'

export interface FilterProps<T> {
    sx?: SxProps
    setSelectedOptions: Dispatch<SetStateAction<T[]>>
    options: T[]
    filterName: string
    transformLabel?: (label: T) => string
    preselected?: T[]
    singleSelect?: boolean
    enableSearch?: boolean
    searchPlaceholder?: string
}

export interface CheckboxItem<T> {
    name: T
    checked: boolean
}

const MultiselectFilterNew = <T,>({
    setSelectedOptions,
    options,
    transformLabel,
    preselected,
    singleSelect,
    enableSearch = false,
    searchPlaceholder = 'Start typing',
}: FilterProps<T>) => {
    const [localSelected, setLocalSelected] = useState<CheckboxItem<T>[]>()
    const [searchTerm, setSearchTerm] = useState('')

    const handleChange = (selectedItem: CheckboxItem<T>): void => {
        setLocalSelected((oldItems) => {
            const updatedItems = oldItems?.map((item) => ({
                name: item.name,
                checked:
                    item.name === selectedItem.name
                        ? !item.checked
                        : singleSelect
                          ? false
                          : item.checked,
            }))

            setSelectedOptions(
                updatedItems?.filter((item) => item.checked).map((item) => item.name) ?? [],
            )
            return updatedItems
        })
    }

    useEffect(() => {
        if (preselected) {
            const selectedOption = options.map((option) => ({
                name: option,
                checked: preselected.includes(option),
            }))
            setLocalSelected(selectedOption)
        }
        // We only want to run this when preselected changes, not when options, filterName, or transformLabel change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preselected])

    const filteredItems = useMemo(() => {
        if (!enableSearch || !searchTerm.trim()) {
            return localSelected
        }

        const lowerSearchTerm = searchTerm.toLowerCase()
        return localSelected?.filter((item) => {
            const label = transformLabel ? transformLabel(item.name) : item.name
            return String(label).toLowerCase().includes(lowerSearchTerm)
        })
    }, [localSelected, searchTerm, enableSearch, transformLabel])

    return (
        <>
            {enableSearch && (
                <Box
                    sx={{
                        padding: '8px 16px',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'white',
                        zIndex: 1,
                    }}
                >
                    <InputNew
                        wide
                        size={'sm'}
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        startAdornment={<SearchOutlinedIcon />}
                    />
                </Box>
            )}
            {filteredItems?.map((filter, i) => {
                const label = transformLabel ? transformLabel(filter.name) : filter.name
                return (
                    <FilterItem
                        key={i}
                        checked={filter.checked}
                        label={String(label)}
                        onToggle={() => handleChange(filter)}
                    />
                )
            })}
        </>
    )
}

export default MultiselectFilterNew
